/**
 * KPI Card Component
 * Componente moderno para exibição de KPIs com visualização aprimorada
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';

interface KPICardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  loading?: boolean;
  trend?: {
    value: number;
    label?: string;
  };
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  formatValue?: (value: number | string) => string;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  loading,
  trend,
  icon,
  color = 'primary',
  formatValue
}) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main
  };

  const selectedColor = colorMap[color];
  const bgColor = alpha(selectedColor, 0.08);

  const formatDisplayValue = (val: number | string): string => {
    if (formatValue) {
      return formatValue(val);
    }
    if (typeof val === 'number') {
      return val.toLocaleString('pt-BR');
    }
    return String(val);
  };

  const renderTrend = () => {
    if (!trend) return null;

    const isPositive = trend.value > 0;
    const isNegative = trend.value < 0;

    const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Remove;
    const trendColor = isPositive ? 'success.main' : isNegative ? 'error.main' : 'text.secondary';

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 1,
          gap: 0.5
        }}
      >
        <TrendIcon
          sx={{
            fontSize: 16,
            color: trendColor
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: trendColor,
            fontWeight: 600
          }}
        >
          {trend.value > 0 ? '+' : ''}
          {trend.value}%{trend.label && ` ${trend.label}`}
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8]
        },
        background: `linear-gradient(135deg, ${bgColor} 0%, ${alpha(selectedColor, 0.02)} 100%)`,
        border: `1px solid ${alpha(selectedColor, 0.2)}`
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: 1,
                textTransform: 'uppercase'
              }}
            >
              {title}
            </Typography>
          </Box>
          {icon && (
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(selectedColor, 0.1),
                color: selectedColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={60}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: selectedColor,
                mb: subtitle ? 0.5 : 0,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              {formatDisplayValue(value)}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.5
                }}
              >
                {subtitle}
              </Typography>
            )}

            {renderTrend()}
          </>
        )}
      </CardContent>
    </Card>
  );
};
